import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronDownIcon, ChevronUpIcon, BadgeNewIcon } from 'src/packages/icons';
import { useTheme } from 'src/packages/theme';
import { NavLinkWithDropdownProps } from '../NavLink.types';
import { LinkWrapper, Collapse, getNavlinkWithDropdownStyles } from './NavLinkWithDropdown.styles';
import { ExternalLink, Flex, Skeleton, Text } from 'src/packages/core';

const NavLinkWithDropdown: React.FC<NavLinkWithDropdownProps> = ({
  sublinks,
  title,
  theme = 'dark',
  highlightSublinks = false,
  p,
  padding,
  px,
  py,
  paddingY,
  paddingX,
  loading = false,
}: NavLinkWithDropdownProps) => {
  const contextTheme = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const { color, iconColor, sublinksColor, sublinksWrapperBgColor } = useMemo(() => {
    return getNavlinkWithDropdownStyles(highlightSublinks, theme);
  }, [highlightSublinks, theme]);

  const toggleCollapse = () => {
    setOpen(!open);
  };

  return (
    <LinkWrapper color={color}>
      <Flex
        alignItems='center'
        justifyContent='space-between'
        width={'100%'}
        onClick={toggleCollapse}
        p={p ?? padding ?? 4}
        px={!p && !padding ? px : paddingX ? paddingX : 4}
        py={!p && !padding ? py : paddingY ? paddingY : 4}
      >
        <Text variant={'p2'} text={t(title)} color={color} />
        {open ? <ChevronUpIcon color={iconColor} /> : <ChevronDownIcon color={iconColor} />}
      </Flex>

      <Collapse className={open ? 'expanded' : 'collapsed'}>
        <Flex flexDirection={'column'} width={'100%'} backgroundColor={sublinksWrapperBgColor}>
          {loading ? (
            <Flex flexDirection={'column'} width={'100%'} gap={2}>
              {sublinks.map((_, index) => (
                <Flex key={index} py={2} px={8}>
                  <Skeleton width={'64px'} height={'20px'} theme={'dark'} />
                </Flex>
              ))}
            </Flex>
          ) : (
            sublinks.map((sl, index) => {
              if (sl.restricted && sl.restricted()) {
                return null;
              }

              return (
                <ExternalLink link={sl.url ?? ''} key={index} py={4} px={8} target={sl.target}>
                  <Flex position={'relative'} gap={'5px'}>
                    <Text
                      variant={'p2'}
                      style={{ whiteSpace: 'nowrap' }}
                      text={t(sl.label)}
                      color={sublinksColor}
                    />

                    {sl.new && (
                      <BadgeNewIcon
                        classNames={theme === 'dark' ? 'orange' : 'red'}
                        style={{
                          top: '-4px',
                          ...(contextTheme.rtl && { left: -25 }),
                          ...(!contextTheme.rtl && { right: -25 }),
                        }}
                      />
                    )}
                  </Flex>
                </ExternalLink>
              );
            })
          )}
        </Flex>
      </Collapse>
    </LinkWrapper>
  );
};

export default NavLinkWithDropdown;
